/**
 * A win opens the report when the board's win wave finishes (a callback from
 * the board), not on a fixed timeout. If no live board is mounted to run the
 * wave, the report opens at once. The safety timer only covers a board that
 * never reports back. A held report belongs to the game that produced it: if
 * another game starts (or the game resets) before it is released, it is
 * dropped rather than opened over the new game.
 */
import { useModalStore } from "../stores/useModalStore";
import type { GameReport } from "../utils/gameReportUtils";
import { useGameplayStore } from "./stores/useGameplayStore";

export const WIN_REPORT_SAFETY_MS = 2500;

let celebrants = 0;
let stopWatching: (() => void) | null = null;

function belongsToCurrentGame(report: GameReport): boolean {
  const { gameStatus, gameReport } = useGameplayStore.getState();
  return gameStatus === "won" && gameReport?.id === report.id;
}

/** Opens the held report if it is still the current game's; else drops it. */
function releaseHeld(reportId?: string): void {
  const modal = useModalStore.getState();
  const held = modal.heldGameReport;
  if (!held || (reportId !== undefined && held.id !== reportId)) return;
  stopWatching?.();
  stopWatching = null;
  if (belongsToCurrentGame(held)) modal.releaseHeldGameReport(held.id);
  else modal.discardHeldGameReport();
}

/** A live board registers while mounted; returns the unregister function. */
export function registerWinCelebrant(): () => void {
  celebrants++;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    celebrants = Math.max(0, celebrants - 1);
    // A board leaving mid-wave must not strand the report.
    if (celebrants === 0) releaseHeld();
  };
}

export function presentGameReport(
  report: GameReport,
  result: "won" | "given_up",
): void {
  const modal = useModalStore.getState();
  if (result !== "won" || celebrants === 0) {
    modal.showGameReportModal(report);
    return;
  }
  modal.holdGameReport(report);
  // Drop the held report as soon as the game it belongs to is replaced.
  stopWatching?.();
  stopWatching = useGameplayStore.subscribe(() => {
    if (useModalStore.getState().heldGameReport?.id !== report.id) return;
    if (!belongsToCurrentGame(report)) releaseHeld(report.id);
  });
  setTimeout(() => releaseHeld(report.id), WIN_REPORT_SAFETY_MS);
}

/** Called by the board when its win wave has finished. */
export function finishWinCelebration(): void {
  releaseHeld();
}
